// src/components/common/HelpPopover.tsx
import { useState, MouseEvent } from 'react';
import { IconButton, Popover, Box, Typography } from '@mui/material';
import { HelpOutline as HelpIcon } from '@mui/icons-material';

interface HelpPopoverProps {
  /** Help text to display */
  text: string;
  /** Optional title */
  title?: string;
  /** Icon size */
  size?: 'small' | 'medium';
}

/**
 * Simple contextual help component.
 * Shows a question mark icon that opens a simple white popover with black text.
 */
export const HelpPopover = ({ text, title, size = 'small' }: HelpPopoverProps) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const handleClick = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  return (
    <>
      <IconButton
        onClick={handleClick}
        size={size}
        sx={{
          p: 0.5,
          color: 'text.secondary',
          '&:hover': {
            color: 'text.primary',
            backgroundColor: 'action.hover',
          },
        }}
      >
        <HelpIcon sx={{ fontSize: size === 'small' ? 18 : 22 }} />
      </IconButton>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        slotProps={{
          paper: {
            sx: {
              p: 2,
              maxWidth: 320,
              boxShadow: 3,
              borderRadius: 1,
            },
          },
        }}
      >
        <Box>
          {title && (
            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 0.5 }}>
              {title}
            </Typography>
          )}
          <Typography variant="body2" color="text.primary">
            {text}
          </Typography>
        </Box>
      </Popover>
    </>
  );
};

export default HelpPopover;
